import { Button, Grid } from "@mui/material";
import { useCallback, useState } from "react";
import { FilterMenu } from "./FilterMenu";
import { useTagFilter } from "./Hooks/useTagFilterHook";
import { useTimeFilter } from "./Hooks/useTimeFilterHook";
import { SavedFilters } from "./SavedFilters";
import { SaveSearchFilters } from "./SaveSearchFilters";
import { useSavedSearchesQuery } from "../../Queries/FilterQueries";
import { IClient } from "../../Typings/Client";
import { ITimeFilter } from "../../Typings/SearchFilters";

interface ISearchFiltersProps {
  isLoading?: boolean;
  tags: string[];
  timeFilter: ITimeFilter;
  setTags: (tags: string[]) => void;
  setTimeFilter: (timeFilter: ITimeFilter) => void;
  filterQueryString: string;
  id?: string;
  selectedClient?: IClient;
}

export default function SearchFilters({
  isLoading = false,
  tags,
  timeFilter,
  setTags,
  setTimeFilter,
  filterQueryString,
  id: htmlId,
  selectedClient,
}: ISearchFiltersProps) {
  const [savedDialogOpen, setSavedDialogOpen] = useState<boolean>(false);
  const [saveNewFilterDialogOpen, setSaveNewFilterDialogOpen] = useState<boolean>(false);
  const { timeLabel, handleTimeChange, availableTimeOptions } = useTimeFilter(timeFilter, setTimeFilter);
  const { searchableTags, tagLabel, handleTagChange } = useTagFilter(tags, setTags, selectedClient?.clientId);
  const { data: savedSearchFilters = [] } = useSavedSearchesQuery(selectedClient?.clientId);

  const hasFilters = useCallback(
    () => (timeFilter && timeFilter.name !== "All Time") || (tags && tags.length > 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tags.length, timeFilter.name],
  );

  const filterExists = useCallback(
    () => savedSearchFilters.filter((f) => f.searchString === filterQueryString).length > 0,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [savedSearchFilters],
  );

  return (
    <>
      <Grid item xs={12} md={4}>
        <FilterMenu
          isLoading={isLoading}
          id="time"
          htmlId={`${htmlId ? `-${htmlId}` : ""}`}
          name="Date added"
          selectedFilters={[timeFilter.name]}
          handleFilterChange={handleTimeChange}
          label={timeLabel}
          availableFilters={availableTimeOptions}
          value={[timeFilter.name]}
          multiple={false}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <FilterMenu
          id="tag"
          isLoading={isLoading}
          htmlId={`${htmlId ? `-${htmlId}` : ""}`}
          name="Tags"
          selectedFilters={tags}
          handleFilterChange={handleTagChange}
          label={tagLabel()}
          availableFilters={searchableTags}
          value={tags}
          multiple={true}
        />
      </Grid>
      <Grid container item xs={12} md={4} data-test={"filter-menu-" + htmlId} spacing={1}>
        <Grid item xs={12} style={{ paddingTop: "15px" }}>
          <Button
            title="Load saved search filter"
            type="button"
            onClick={() => setSavedDialogOpen(true)}
            variant="text"
            data-test="filter-button-savedfilters"
            disabled={isLoading || !savedSearchFilters || savedSearchFilters.length <= 0}
          >
            load
          </Button>
          |
          <Button
            title="Save search filter"
            disabled={!hasFilters() || filterExists() || isLoading}
            onClick={() => setSaveNewFilterDialogOpen(true)}
            type="button"
            variant="text"
            data-test="filter-button-save-filters"
          >
            save
          </Button>
        </Grid>
      </Grid>
      <SavedFilters
        setTagFilters={setTags}
        setTimeFilter={setTimeFilter}
        dialogOpen={savedDialogOpen}
        handleDialogClose={() => setSavedDialogOpen(false)}
        savedSearchFilters={savedSearchFilters}
        clientId={selectedClient?.clientId}
      />
      <SaveSearchFilters
        queryString={filterQueryString ?? ""}
        dialogOpen={saveNewFilterDialogOpen}
        handleDialogClose={() => setSaveNewFilterDialogOpen(false)}
        clientId={selectedClient?.clientId}
      />
    </>
  );
}
